import React from "react";
import { Link } from "gatsby";

import Header from "../components/Header";
import Footer from "../components/Footer";
import HelpfulFirstStep from "../components/HelpfulFirstStep";
import Seo from "../components/Seo";
import TrustpilotInline from "../components/TrustpilotInline";
import { SINGLE_TIER_ONE } from "../constants/pricing";
import logoPrimary from "../images/logos/logo_primary.svg";
import "../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const supportAreas = [
	[
		"livein-icon-04.png",
		"Medication",
		"Prompts, support and safe administration in line with the care plan.",
	],
	[
		"livein-icon-07.png",
		"Meals and nutrition",
		"Shopping and home-cooked meals shaped around preferences and dietary needs.",
	],
	[
		"livein-icon-05.png",
		"Personal care",
		"Discreet help with washing, dressing, continence and daily routines.",
	],
	[
		"livein-icon-02.png",
		"Mobility",
		"Reassuring support to move safely around the home and get out and about.",
	],
	[
		"livein-icon-03.png",
		"Household tasks",
		"Light housekeeping, laundry and the everyday jobs that keep a home comfortable.",
	],
	[
		"livein-icon-06.png",
		"Companionship",
		"Conversation, shared interests and company that feels natural rather than scheduled.",
	],
	[
		"livein-icon-01.png",
		"Appointments and outings",
		"Help attending appointments, seeing friends and staying part of the local community.",
	],
	[
		"livein-icon-07.png",
		"Routines and wellbeing",
		"A consistent rhythm to the day, with changes noticed and shared with the care team.",
	],
];

const managedCareRoles = [
	[
		"Professional carer",
		"Provides one-to-one support in the home and follows the personalised care plan.",
	],
	[
		"Care Manager",
		"Assesses needs, oversees quality and reviews care as circumstances change.",
	],
	[
		"Care coordination",
		"Supports continuity, manages placements and helps arrange planned cover.",
	],
	[
		"Family visibility",
		"Keeps the people who matter informed without making technology a barrier.",
	],
];

const comparisonRows = [
	["Trained carers", true, false, true],
	["One-to-one support", false, true, true],
	["Dedicated care team", false, true, true],
	["Comfort of your own home", false, true, true],
	["Connect to your community", false, true, true],
	["Oversight of care, nutrition and medication", true, false, true],
	["Understanding of wellbeing", false, false, true],
	["Fully managed", true, false, true],
	["Family can stay at any time", false, true, true],
];

const included = [
	"A personalised care assessment and plan",
	"Carefully selected and vetted professional carers",
	"A dedicated Care Manager",
	"Ongoing care coordination and quality oversight",
	"Planned carer rotation and continuity support",
	"Family access to relevant care information",
];

const familyProvides = [
	"A private bedroom for the professional carer",
	"Food or an agreed food allowance",
	"Reasonable breaks and time away from duties",
	"A safe, welcoming home environment",
];

const faqs = [
	[
		"How quickly can live-in care start?",
		"Timescales depend on the assessment, care needs and the right carer match. In urgent situations, Edyn will explain what is realistically possible and prioritise a safe start rather than making promises before understanding the situation.",
	],
	[
		"Is night-time care included?",
		"A live-in carer can provide limited support during the night, but they must also have enough uninterrupted rest to work safely. Frequent or prolonged waking may require a waking-night carer or a different care arrangement, which will be discussed during assessment.",
	],
	[
		"Will we always have the same carer?",
		"Care is usually delivered by a small, planned team rather than one person indefinitely. This gives the main carer proper breaks and creates resilient continuity when someone is on leave or unavailable.",
	],
	[
		"Can a live-in carer support dementia care?",
		"Yes. Many families choose live-in care because familiar surroundings and routines can be especially valuable for someone living with dementia. The assessment establishes the right skills, risks and level of support.",
	],
	[
		"Does the carer need their own room?",
		"Yes. A professional carer needs a clean, private bedroom with suitable storage and access to bathroom facilities. Their room is an important part of providing safe and sustainable live-in care.",
	],
	[
		"Is Edyn regulated by the CQC?",
		"Yes. Edyn's managed care service is registered with the Care Quality Commission in England. This means the service is accountable for how care is assessed, planned, delivered and reviewed.",
	],
];

const CompareMark = ({ available }) => (
	<span
		className={`livein-compare__mark ${available ? "is-yes" : "is-no"}`}
		aria-label={available ? "Included" : "Not included"}
	>
		<span aria-hidden="true">{available ? "\u2713" : "\u00d7"}</span>
	</span>
);

export default function LiveInCare() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Fully Managed Live-in Care | Edyn Care"
				description="Understand Edyn's fully managed live-in care, what a professional carer can help with, what is included, pricing and how care starts."
				path="/liveincare/"
				service={{ name: "Fully managed live-in care" }}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{ name: "Live-in care", path: "/liveincare/" },
				]}
			/>
			<Header />

			<main id="main-content" className="edyn-livein">
				<section className="livein-editorial-hero">
					<div className="site-shell livein-editorial-hero__grid">
						<div className="livein-editorial-hero__copy">
							<p className="eyebrow">Fully managed live-in care</p>
							<h1>Care at home, shaped around a life.</h1>
							<p className="livein-editorial-hero__lead">
								A professional carer lives in the home and provides one-to-one
								support, companionship and reassurance, while an experienced
								Edyn care team manages the details behind the scenes.
							</p>
							<div className="button-row">
								<Link className="button button--primary" to="/care-consultation/">
									Arrange a call
								</Link>
								<Link className="button button--text" to="/pricing/">
									View pricing <span aria-hidden="true">&#8594;</span>
								</Link>
							</div>
							<a
								className="livein-editorial-hero__phone"
								href="tel:02039709900"
							>
								Prefer to talk now? <strong>020 3970 9900</strong>
							</a>
						</div>

						<div className="livein-editorial-hero__visual">
							<img
								src={`${imagePath}/live-in-image-01.jpg`}
								alt="A professional carer and an older woman enjoying time together at home"
								width="1327"
								height="1115"
							/>
							<div className="livein-editorial-hero__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Stay at home.</strong>
									<br />
									Keep life feeling familiar.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="livein-proof" aria-label="Edyn service highlights">
					<div className="site-shell livein-proof__grid">
						<p>
							<strong>CQC regulated</strong>
							<span>Managed care in England</span>
						</p>
						<p>
							<strong>One-to-one</strong>
							<span>Support shaped around one person</span>
						</p>
						<p>
							<strong>A whole team</strong>
							<span>Ongoing oversight and coordination</span>
						</p>
					</div>
				</section>

				<section className="livein-meaning section-space">
					<div className="site-shell livein-meaning__grid">
						<div>
							<p className="eyebrow">What live-in care means</p>
							<h2>A professional presence. A familiar way of life.</h2>
						</div>
						<div className="livein-meaning__copy">
							<p className="section-lead">
								Live-in care offers practical help without asking someone to
								leave behind the home, routines and relationships that matter.
							</p>
							<p>
								The carer has their own room and shares daily life in a way that
								respects privacy and independence. Support might be light at
								first or more involved, and the plan can adapt as needs change.
							</p>
							<Link className="text-link" to="/approach/">
								See how Edyn arranges care{" "}
								<span aria-hidden="true">&#8594;</span>
							</Link>
						</div>
					</div>
				</section>

				<section className="livein-support section-space section-soft">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Everyday support</p>
								<h2>What can a live-in carer help with?</h2>
							</div>
							<p>
								The assessment turns individual needs, preferences and risks
								into a clear care plan. These are common areas of support, not a
								one-size-fits-all checklist.
							</p>
						</div>
						<div className="livein-support__grid">
							{supportAreas.map(([icon, title, description], index) => (
								<article key={title}>
									<div className="livein-support__icon">
										<img src={`${imagePath}/${icon}`} alt="" loading="lazy" />
									</div>
									<span>{String(index + 1).padStart(2, "0")}</span>
									<div className="livein-support__copy">
										<h3>{title}</h3>
										<p>{description}</p>
									</div>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="livein-life section-space">
					<div className="site-shell livein-life__grid">
						<div className="livein-life__visual">
							<img
								src={`${imagePath}/livein-imageclus-03.jpg`}
								alt="An older man talking with his carer at home"
								loading="lazy"
							/>
							<div className="livein-life__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Support where needed.</strong>
									Independence everywhere possible.
								</p>
							</div>
						</div>
						<div className="livein-life__copy">
							<p className="eyebrow">Care around the person</p>
							<h2>The aim is not to take over. It is to make more possible.</h2>
							<ul>
								<li>
									<strong>Keep meaningful routines</strong>
									<span>
										Mornings, meals and rituals can stay recognisable.
									</span>
								</li>
								<li>
									<strong>Remain connected</strong>
									<span>Friends, family, pets and community stay close.</span>
								</li>
								<li>
									<strong>Protect independence</strong>
									<span>
										Support is given where needed, not where it is not.
									</span>
								</li>
							</ul>
							<TrustpilotInline className="livein-life__trustpilot-inline" />
						</div>
					</div>
				</section>

				<section className="livein-managed section-space section-dark">
					<div className="site-shell livein-managed__grid">
						<div className="livein-managed__copy">
							<p className="eyebrow eyebrow--light">Fully managed by Edyn</p>
							<h2>One carer in the home. A complete care team behind them.</h2>
							<p>
								Edyn is responsible for more than making an introduction. We
								assess, plan, coordinate and review care, supporting both the
								family and the professional carer throughout the placement.
							</p>
							<Link className="button button--cream" to="/approach/">
								Explore how Edyn works
							</Link>
						</div>
						<div className="livein-managed__roles">
							{managedCareRoles.map(([title, description], index) => (
								<article key={title}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									<h3>{title}</h3>
									<p>{description}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="livein-included section-space">
					<div className="site-shell">
						<div className="section-heading">
							<p className="eyebrow">Practical details</p>
							<h2>Clear expectations make care work well.</h2>
						</div>
						<div className="livein-included__grid">
							<article>
								<p className="livein-included__label">Edyn manages</p>
								<h3>Care, coordination and oversight</h3>
								<ul>
									{included.map((item) => (
										<li key={item}>{item}</li>
									))}
								</ul>
							</article>
							<article className="livein-included__home">
								<p className="livein-included__label">The household provides</p>
								<h3>A sustainable place to live and work</h3>
								<ul>
									{familyProvides.map((item) => (
										<li key={item}>{item}</li>
									))}
								</ul>
								<Link
									className="text-link"
									to="/blog/what-does-a-live-in-carer-need-in-your-home/"
								>
									Prepare the home <span aria-hidden="true">&#8594;</span>
								</Link>
							</article>
						</div>
					</div>
				</section>

				<section className="livein-compare section-space section-clay">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Understanding the options</p>
								<h2>Live-in care compared with a care home.</h2>
							</div>
							<p>
								See how a care home, an introductory matching agency and Edyn's
								fully managed service differ in the areas families often ask
								about.
							</p>
						</div>
						<div className="livein-compare__scroll" tabIndex="0">
							<table className="livein-compare__table">
								<caption className="visually-hidden">
									Comparison of care homes, introductory agencies and Edyn Care
								</caption>
								<thead>
									<tr>
										<th scope="col">How we compare</th>
										<th scope="col">Care home</th>
										<th scope="col">Introductory agencies</th>
										<th className="is-edyn" scope="col">
											<img
												className="livein-compare__edyn-mark"
												src={logoPrimary}
												alt="Edyn Care"
											/>
										</th>
									</tr>
								</thead>
								<tbody>
									{comparisonRows.map(([label, careHome, agency, edyn]) => (
										<tr key={label}>
											<th scope="row">{label}</th>
											<td data-label="Care home">
												<CompareMark available={careHome} />
											</td>
											<td data-label="Introductory agency">
												<CompareMark available={agency} />
											</td>
											<td className="is-edyn" data-label="edyn.care">
												<CompareMark available={edyn} />
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<Link
							className="text-link livein-compare__link"
							to="/blog/live-in-care-vs-care-home-which-option-is-right/"
						>
							Read the full comparison <span aria-hidden="true">&#8594;</span>
						</Link>
					</div>
				</section>

				<section className="livein-price section-space">
					<div className="site-shell livein-price__grid">
						<div>
							<p className="eyebrow">Transparent pricing</p>
							<h2>Understand the likely cost before making a decision.</h2>
							<p>
								The weekly price reflects the complexity and level of support
								required. A care assessment allows Edyn to recommend the right
								package and explain exactly what is included.
							</p>
							<Link className="button button--primary" to="/pricing/">
								See pricing and inclusions
							</Link>
						</div>
						<div className="livein-price__figure">
							<span>Live-in care for one person</span>
							<strong>From {SINGLE_TIER_ONE}</strong>
							<p>per week, confirmed following assessment</p>
						</div>
					</div>
				</section>

				<section className="livein-faq-redesign section-space section-soft">
					<div className="site-shell livein-faq-redesign__grid">
						<div>
							<p className="eyebrow">Common questions</p>
							<h2>Useful answers before you begin.</h2>
							<p>
								Every family and home is different. A conversation gives you
								answers grounded in the situation rather than general advice.
							</p>
						</div>
						<div className="livein-faq-redesign__list">
							{faqs.map(([question, answer]) => (
								<details key={question}>
									<summary>{question}</summary>
									<p>{answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>

				<section className="livein-guidance section-space">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Planning and advice</p>
								<h2>Read at your own pace.</h2>
							</div>
							<Link className="text-link" to="/blog/">
								Browse all guidance <span aria-hidden="true">&#8594;</span>
							</Link>
						</div>
						<div className="livein-guidance__grid">
							<Link to="/blog/what-can-a-live-in-carer-help-with-2/">
								<span>Everyday care</span>
								<h3>What can a live-in carer help with?</h3>
								<strong aria-hidden="true">&#8599;</strong>
							</Link>
							<Link to="/blog/live-in-care-vs-care-home-which-option-is-right/">
								<span>Comparing care</span>
								<h3>Live-in care vs a care home</h3>
								<strong aria-hidden="true">&#8599;</strong>
							</Link>
							<Link to="/blog/what-is-the-cost-of-live-in-care-2/">
								<span>Planning costs</span>
								<h3>What is the cost of live-in care?</h3>
								<strong aria-hidden="true">&#8599;</strong>
							</Link>
						</div>
					</div>
				</section>

				<HelpfulFirstStep dark />
			</main>

			<Footer includeCTA={false} />
		</div>
	);
}
